<template>
  <el-dialog
    v-model="localVisible"
    :title="modalTitle"
    width="520px"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    @closed="reset"
  >
    <div style="margin-bottom: 25px">
      {{ modalText }} a partir de la ET Nro <b>{{ codigoET }}</b
      >.
    </div>

    <el-row
      v-if="isGlobal"
      :gutter="12"
      style="margin-bottom: 20px"
    >
      <el-col :span="24">
        <div style="font-weight: bold; margin-bottom: 5px; font-size: 14px">Cliente</div>
        <el-autocomplete
          v-model="form.cliente.Descripcion"
          size="default"
          :value-key="'Descripcion'"
          :class="{ 'input-error': showErrors && !clienteOk }"
          :fetch-suggestions="handleSearch"
          clearable
          placeholder="Buscar e ingresar el cliente..."
          style="width: 100%"
          @select="handleClientSelect"
          @clear="handleClientClear"
        />
        <div
          v-if="showErrors && !clienteOk"
          style="color: red; font-size: 12px; margin-top: 4px"
        >
          El cliente es obligatorio para ETs Globales.
        </div>
      </el-col>
    </el-row>
    <el-row :gutter="12">
      <custom-select
        v-model="form.unidadVenta"
        :label="'Unidad venta'"
        :spanColumn="12"
        :settings="{
          placeholder: 'Seleccionar unidad',
          options: unidadesMedida,
        }"
      />
      <custom-input-number
        v-model="form.cantidad"
        :label="'Cantidad'"
        :spanColumn="8"
        :settings="{
          placeholder: 'Cantidad',
          min: cantidadMinima,
          precision,
          step: 1,
          controls: false,
        }"
      />
    </el-row>

    <template #footer>
      <el-button
        size="default"
        @click="localVisible = false"
        >Cancelar</el-button
      >
      <el-button
        type="primary"
        size="default"
        :disabled="!isValid"
        @click="onConfirm"
      >
        Generar
      </el-button>
    </template>
  </el-dialog>
</template>

<script setup>
import {
  computed,
  reactive,
  ref,
  watch,
} from 'vue';
import { storeToRefs } from 'pinia';
import CustomSelect from '@/shared/components/custom_select.vue';
import CustomInputNumber from '@/shared/components/custom_input_number.vue';
import { useUnidadMedidaStore } from '@/stores/unidad_medida/unidad_medida_store.js';
import { useProspectStore } from '@/views/prospects/stores/prospect_store.js';
import {
  precisionCantidad,
  redondearCantidad,
} from '@/shared/utils/cantidad_unidad_venta.js';

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  codigoET: { type: Number, required: true },
  initialCantidad: { type: Number, default: null },
  initialCodigoUnidadVenta: { type: Number, default: null },
  codigoElementoTipo: { type: Number, required: true },
  tipoDocumento: { type: String, default: 'OP' },
  isGlobal: { type: Boolean, default: false },
});

const unidadMedidaStore = useUnidadMedidaStore();
const prospectStore = useProspectStore();
const { unidadesMedida } = storeToRefs(unidadMedidaStore);
const { searchProspectsHTTP } = prospectStore;
const emit = defineEmits(['update:modelValue', 'confirm']);

const localVisible = computed({
  get: () => props.modelValue,
  set: (v) => emit('update:modelValue', v),
});

const modalTitle = computed(() => {
  const titles = {
    COTIZACION: 'Generar Cotización',
    SOLICITUD: 'Generar Solicitud',
    OP: 'Generar Orden de Producción',
  };
  return titles[props.tipoDocumento] || 'Generar Documento';
});

const modalText = computed(() => {
  const texts = {
    COTIZACION: 'Se generará una Cotización',
    SOLICITUD: 'Se generará una Solicitud',
    OP: 'Se generará una OP',
  };
  return texts[props.tipoDocumento] || 'Se generará un documento';
});

const showErrors = ref(false);

const form = reactive({
  cantidad: null,
  unidadVenta: null,
  cliente: { Descripcion: '' },
  codigoCliente: null,
});

const precision = computed(() => precisionCantidad(form.unidadVenta?.Codigo));
const cantidadMinima = computed(() => (precision.value > 0 ? 10 ** -precision.value : 1));
const cantidadOk = computed(
  () => typeof form.cantidad === 'number' && form.cantidad >= cantidadMinima.value,
);

watch(precision, (nueva) => {
  if (typeof form.cantidad === 'number') form.cantidad = redondearCantidad(form.cantidad, nueva);
});
const unidadOk = computed(() => !!form.unidadVenta?.Codigo);
const clienteOk = computed(() => !props.isGlobal || !!form.codigoCliente);
const isValid = computed(() => cantidadOk.value && unidadOk.value && clienteOk.value);

const handleSearch = async (query, cb) => {
  let results = [];
  if (query && query.length >= 3 && query !== 'null') {
    results = await searchProspectsHTTP(query);
  }
  cb(results);
};

const handleClientSelect = (item) => {
  if (item && item.Codigo) {
    form.cliente = item;
    form.codigoCliente = item.Codigo;
  }
};

const handleClientClear = () => {
  form.cliente = { Descripcion: '' };
  form.codigoCliente = null;
};

const reset = () => {
  showErrors.value = false;
  form.cantidad = props.initialCantidad ?? null;
  handleClientClear();
  if (props.initialCodigoUnidadVenta) {
    form.unidadVenta =
      unidadesMedida.value.find((u) => u?.Codigo === props.initialCodigoUnidadVenta) ?? null;
  } else {
    form.unidadVenta = null;
  }
};

const loadUnidadesVenta = async () => {
  unidadMedidaStore.emptyList();
  await unidadMedidaStore.findAllUnidadMedidaHTTP({
    page: 0,
    Activo: true,
    EsUnidadVenta: true,
    CodigoElementoTipo: props.codigoElementoTipo,
  });
};

watch(
  () => props.modelValue,
  async (open) => {
    if (!open) return;
    await loadUnidadesVenta();
    reset();
  },
);

const onConfirm = () => {
  showErrors.value = true;
  if (!isValid.value) return;

  emit('confirm', {
    codigoET: props.codigoET,
    cantidad: form.cantidad,
    codigoUnidadVenta: form.unidadVenta.Codigo,
    codigoClienteDestino: props.isGlobal ? form.codigoCliente : null,
  });
};
</script>
